/// Combined auth + current-player state, covering
/// mobile/lib/auth/handler/auth_handler.dart and
/// mobile/lib/user/handler/user_handler.dart.
///
/// Those are two Riverpod notifiers where the user handler watches the auth
/// one. A single Zustand store expresses the same dependency without the
/// indirection, since the derived value is just "signed-in user or guest".

import { create } from 'zustand';

import { appEnv } from '@/app/env/appEnv';
import { ApiError } from '@/app/network/apiClient';
import * as authRepository from '@/auth/data/authRepository';
import { requestGoogleIdToken, SocialSignInCancelled } from '@/auth/data/googleSignIn';
import type { SubmitGameRunRequest } from '@/game/data/gameRunRepository';
import * as gameRunRepository from '@/game/data/gameRunRepository';
import { useGameRunStore } from '@/game/handler/gameRunStore';
import type { EmailStatus } from '@/auth/domain/emailStatus';
import { clearAnonymousHighScore, readAnonymousHighScore, writeAnonymousHighScore } from '@/user/data/userLocalData';
import * as userRepository from '@/user/data/userRepository';
import type { User } from '@/user/domain/user';
import { anonymousUser } from '@/user/domain/user';
import { useGuestScoreMergeStore } from '@/user/handler/guestScoreMergeStore';

interface AuthState {
  user: User;
  isRestoring: boolean;
  isSubmitting: boolean;
  error: string | null;

  restore: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<boolean>;
  signUp: (email: string, password: string, proofToken: string, username?: string) => Promise<boolean>;
  signInWithGoogle: () => Promise<'done' | 'needsUsername' | false>;
  lookupEmail: (email: string) => Promise<EmailStatus>;
  setPassword: (password: string) => Promise<void>;
  changeUsername: (username: string) => Promise<void>;
  requestChangeEmail: (email: string) => Promise<number>;
  confirmChangeEmail: (email: string, code: string) => Promise<void>;
  signOut: () => Promise<void>;
  resendVerification: () => Promise<void>;
  verifyOtpForUser: (code: string) => Promise<void>;
  clearError: () => void;

  updateHighScore: (score: number) => Promise<void>;
  submitGameRun: (request: SubmitGameRunRequest) => Promise<void>;
  clearHighScore: () => Promise<void>;
}

function messageOf(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : 'Something went wrong';
}

/** After sign-in: keep account as-is and optionally prompt to bring guest score. */
function finishSignedIn(user: User): User {
  useGuestScoreMergeStore.getState().consider(user.highScore);
  return user;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: anonymousUser(readAnonymousHighScore()),
  isRestoring: true,
  isSubmitting: false,
  error: null,

  restore: async () => {
    set({ isRestoring: true });
    try {
      const restored = await authRepository.restoreSession();
      if (restored) {
        set({ user: finishSignedIn(restored) });
      } else {
        set({ user: anonymousUser(readAnonymousHighScore()) });
      }
    } catch {
      // A failed restore is not worth surfacing: fall back to guest and let
      // the next explicit sign-in report the real problem.
      set({ user: anonymousUser(readAnonymousHighScore()) });
    } finally {
      set({ isRestoring: false });
    }
  },

  signIn: async (email, password) => {
    set({ isSubmitting: true, error: null });
    try {
      set({ user: finishSignedIn(await authRepository.signIn(email, password)) });
      return true;
    } catch (error) {
      set({ error: messageOf(error) });
      return false;
    } finally {
      set({ isSubmitting: false });
    }
  },

  signUp: async (email, password, proofToken, username) => {
    set({ isSubmitting: true, error: null });
    try {
      set({
        user: finishSignedIn(
          await authRepository.signUp(email, password, proofToken, username),
        ),
      });
      return true;
    } catch (error) {
      set({ error: messageOf(error) });
      return false;
    } finally {
      set({ isSubmitting: false });
    }
  },

  signInWithGoogle: async () => {
    if (!appEnv.googleClientId) {
      set({ error: 'Google Sign-In is not configured' });
      return false;
    }
    set({ isSubmitting: true, error: null });
    try {
      const idToken = await requestGoogleIdToken(appEnv.googleClientId);
      const result = await authRepository.signInWithGoogle(idToken);
      set({ user: finishSignedIn(result.user) });
      return result.needsUsername ? 'needsUsername' : 'done';
    } catch (error) {
      if (error instanceof SocialSignInCancelled) return false;
      set({ error: messageOf(error) });
      return false;
    } finally {
      set({ isSubmitting: false });
    }
  },

  lookupEmail: async (email) => {
    return authRepository.emailStatus(email);
  },

  setPassword: async (password) => {
    const updated = await authRepository.setPassword(password);
    set({ user: updated });
  },

  changeUsername: async (username) => {
    const { user } = get();
    if (user.isAnonymous) {
      throw new Error('Guests cannot change username');
    }
    set({ user: await userRepository.updateUsername(username) });
  },

  requestChangeEmail: async (email) => {
    const { user } = get();
    if (user.isAnonymous) {
      throw new Error('Guests cannot change email');
    }
    return authRepository.requestChangeEmail(email);
  },

  confirmChangeEmail: async (email, code) => {
    const { user } = get();
    if (user.isAnonymous) {
      throw new Error('Guests cannot change email');
    }
    set({ user: await authRepository.confirmChangeEmail(email, code) });
  },

  signOut: async () => {
    set({ isSubmitting: true, error: null });
    try {
      await authRepository.signOut();
    } catch (error) {
      set({ error: messageOf(error) });
    } finally {
      set({ user: anonymousUser(readAnonymousHighScore()), isSubmitting: false });
    }
  },

  resendVerification: async () => {
    await authRepository.resendVerification();
  },

  verifyOtpForUser: async (code) => {
    set({ user: await authRepository.verifyOtpForUser(code) });
  },

  clearError: () => set({ error: null }),

  updateHighScore: async (score) => {
    const { user } = get();
    if (score <= user.highScore) return;

    if (user.isAnonymous) {
      writeAnonymousHighScore(score);
      set({ user: { ...user, highScore: score } });
      return;
    }

    try {
      set({ user: await userRepository.updateHighScore(score) });
    } catch (error) {
      // The run already happened; keep the local number rather than losing it
      // to a transient network failure.
      set({ user: { ...user, highScore: score }, error: messageOf(error) });
    }
  },

  submitGameRun: async (request) => {
    const { user } = get();
    if (user.isAnonymous) {
      // Guests have no API run history — persist high score locally only.
      await get().updateHighScore(request.score);
      return;
    }

    try {
      const response = await gameRunRepository.submitGameRun(request);
      set({ user: response.user });
      await useGameRunStore.getState().refresh();
    } catch (error) {
      console.debug('submitGameRun failed:', error);
      await get().updateHighScore(request.score);
    }
  },

  /// Dev-only for guests. Signed-in scores live on the API and cannot be reset.
  clearHighScore: async () => {
    const { user } = get();
    if (!user.isAnonymous) {
      set({ error: 'Clearing a signed-in high score is not supported by the API yet' });
      return;
    }
    clearAnonymousHighScore();
    set({ user: { ...user, highScore: 0 } });
  },
}));
